import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useAuth } from '../hooks/useAuth';
import { useDemoMode } from '../hooks/useDemoMode';
import { buildApiUrl } from '../config/api';
import LayerSymbologyIcon from './LayerSymbologyIcon';

// Set up Mapbox token
const accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
console.log('SharedMapViewer - Mapbox access token loaded:', accessToken ? 'Yes' : 'No');
mapboxgl.accessToken = accessToken;

const SharedMapViewer = ({ sharedMap, shareId, password = null }) => {
  const { makeAuthenticatedRequest, isAuthenticated } = useAuth();
  const { isDemoMode } = useDemoMode();
  const mapContainer = useRef(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [layers, setLayers] = useState([]);
  const [layerVisibility, setLayerVisibility] = useState(new Map());
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, layer: null });
  const contextMenuRef = useRef(null);
  const [isPanelOpen, setIsPanelOpen] = useState(typeof window !== 'undefined' ? window.innerWidth > 768 : true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    // Use a timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      if (sharedMap && mapContainer.current && !map) {
        initializeMap();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [sharedMap, map]);

  // Track mobile breakpoint and default panel behavior
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile && isPanelOpen) {
        // Prefer closed by default on mobile
        setIsPanelOpen(false);
      }
      if (!mobile && !isPanelOpen) {
        // Prefer open by default on desktop
        setIsPanelOpen(true);
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isPanelOpen]);

  // Keep map canvas sized correctly on mobile orientation/resize
  useEffect(() => {
    if (!map) return;
    const handleResize = () => {
      try { map.resize(); } catch (e) {}
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [map]);

  useEffect(() => {
    // Close context menu when clicking outside
    const handleClickOutside = (event) => {
      if (contextMenu.visible && contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ visible: false, x: 0, y: 0, layer: null });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible]);

  const initializeMap = async () => {
    try {
      setLoading(true);
      setError('');

      // Initialize Mapbox map
      if (!mapboxgl) {
        throw new Error('Mapbox GL JS not loaded');
      }

      // Check if Mapbox token is set
      if (!mapboxgl.accessToken) {
        throw new Error('Mapbox access token not configured');
      }
      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: `mapbox://styles/mapbox/${sharedMap.map_state.style}`,
        center: [sharedMap.map_state.center[0], sharedMap.map_state.center[1]],
        zoom: sharedMap.map_state.zoom,
        bearing: sharedMap.map_state.bearing,
        pitch: sharedMap.map_state.pitch,
        projection: 'mercator'
      });

      mapInstance.on('load', async () => {
        setMap(mapInstance);
        await loadLayers(mapInstance);
        setLoading(false);
      });

      mapInstance.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
        setLoading(false);
      });

    } catch (err) {
      console.error('Error initializing map:', err);
      setError(err.message || 'Failed to initialize map');
      setLoading(false);
    }
  };

  const loadLayers = async (mapInstance) => {
    try {
      const visibilityMap = new Map();
      const loadedLayers = [];

      // Sort layers by order
      const sortedLayers = [...sharedMap.layers].sort((a, b) => a.layer_order - b.layer_order);

      for (const layerConfig of sortedLayers) {
        try {
          // Build endpoint with password parameter if needed
          const passwordParam = password ? `?password=${encodeURIComponent(password)}` : '';
          const endpoint = buildApiUrl(`data/maps/shared/${shareId}/datasets/${layerConfig.dataset_id}/geojson${passwordParam}`);
          // In demo mode, convert to demo endpoint
          const finalEndpoint = isDemoMode ? endpoint.replace('/api/', '/api/demo/') : endpoint;
          let response;
          
          // Always try unauthenticated first for shared maps (they should be public)
          try {
            response = await fetch(finalEndpoint);
            
            // If unauthenticated fails and user is authenticated, try authenticated request
            if (!response.ok && isAuthenticated && (response.status === 401 || response.status === 403)) {
              console.log('Unauthenticated layer request failed, trying authenticated...');
              response = await makeAuthenticatedRequest(finalEndpoint);
            }
          } catch (authError) {
            console.log('Authentication failed for layer, falling back to unauthenticated:', authError.message);
            // If authentication fails, fall back to unauthenticated request
            response = await fetch(finalEndpoint);
          }

          if (!response.ok) {
            console.warn(`Failed to load layer ${layerConfig.dataset_id}`);
            continue;
          }

          const layerData = await response.json();
          
          // Add layer to map
          await addLayerToMap(mapInstance, layerConfig, layerData);
          
          // Track visibility
          visibilityMap.set(layerConfig.dataset_id, layerConfig.is_visible);
          
          // Add to loaded layers
          loadedLayers.push({
            ...layerConfig,
            dataset: layerConfig.dataset || { name: `Layer ${layerConfig.dataset_id}` }
          });

        } catch (err) {
          console.error(`Error loading layer ${layerConfig.dataset_id}:`, err);
        }
      }

      setLayers(loadedLayers);
      setLayerVisibility(visibilityMap);

    } catch (err) {
      console.error('Error loading layers:', err);
      setError('Failed to load map layers');
    }
  };

  const addLayerToMap = async (mapInstance, layerConfig, layerData) => {
    const layerId = `layer-${layerConfig.dataset_id}`;
    const sourceId = `source-${layerConfig.dataset_id}`;

    // Remove existing layer/source if any
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getSource(sourceId)) {
      mapInstance.removeSource(sourceId);
    }

    // Handle raster data
    if (layerData.type === 'raster' && layerData.rasterData) {
      await addRasterLayer(mapInstance, layerConfig, layerData.rasterData, layerId, sourceId);
    } else {
      // Handle vector data
      await addVectorLayer(mapInstance, layerConfig, layerData, layerId, sourceId);
    }
  };

  const addRasterLayer = async (mapInstance, layerConfig, rasterData, layerId, sourceId) => {
    const imageData = `data:image/png;base64,${rasterData.preview}`;
    
    mapInstance.addSource(sourceId, {
      type: 'image',
      url: imageData,
      coordinates: [
        [rasterData.bbox[0], rasterData.bbox[3]], // top-left
        [rasterData.bbox[2], rasterData.bbox[3]], // top-right
        [rasterData.bbox[2], rasterData.bbox[1]], // bottom-right
        [rasterData.bbox[0], rasterData.bbox[1]]  // bottom-left
      ]
    });

    mapInstance.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      paint: {
        'raster-opacity': layerConfig.opacity || 1.0
      },
      layout: {
        'visibility': layerConfig.is_visible ? 'visible' : 'none'
      }
    });
  };

  const addVectorLayer = async (mapInstance, layerConfig, geoJsonData, layerId, sourceId) => {
    mapInstance.addSource(sourceId, {
      type: 'geojson',
      data: geoJsonData
    });

    // Get layer style based on geometry type from the layer configuration
    const geometryType = layerConfig.dataset?.geometry_type || 'Point';
    const layerStyles = getLayerStyle(geometryType, layerId, sourceId, layerConfig);
    
    if (Array.isArray(layerStyles)) {
      layerStyles.forEach(style => mapInstance.addLayer(style));
    } else {
      mapInstance.addLayer(layerStyles);
    }
  };

  const getLayerStyle = (geometryType, layerId, sourceId, layerConfig) => {
    const baseStyle = {
      id: layerId,
      source: sourceId,
      layout: {
        'visibility': layerConfig.is_visible ? 'visible' : 'none'
      }
    };

    // Apply custom styling from layerConfig.style_config if available
    const customStyle = layerConfig.style_config || {};
    const opacity = typeof layerConfig.opacity === 'number' ? layerConfig.opacity : 1.0;

    switch (geometryType?.toLowerCase()) {
      case 'point':
      case 'multipoint':
        return {
          ...baseStyle,
          type: 'circle',
          paint: {
            'circle-radius': customStyle['circle-radius'] || 6,
            'circle-color': customStyle['circle-color'] || '#007cbf',
            'circle-stroke-width': customStyle['circle-stroke-width'] || 2,
            'circle-stroke-color': customStyle['circle-stroke-color'] || '#ffffff',
            'circle-opacity': customStyle['circle-opacity'] !== undefined ? customStyle['circle-opacity'] : opacity
          }
        };
      
      case 'linestring':
      case 'multilinestring':
        return {
          ...baseStyle,
          type: 'line',
          paint: {
            'line-color': customStyle['line-color'] || '#007cbf',
            'line-width': customStyle['line-width'] || 3,
            'line-opacity': customStyle['line-opacity'] !== undefined ? customStyle['line-opacity'] : opacity
          }
        };
      
      case 'polygon':
      case 'multipolygon':
        return [
          {
            ...baseStyle,
            id: layerId + '-fill',
            type: 'fill',
            paint: {
              'fill-color': customStyle['fill-color'] || '#007cbf',
              'fill-opacity': customStyle['fill-opacity'] !== undefined ? customStyle['fill-opacity'] : (0.3 * opacity)
            }
          },
          {
            ...baseStyle,
            id: layerId + '-stroke',
            type: 'line',
            paint: {
              'line-color': customStyle['line-color'] || '#007cbf',
              'line-width': customStyle['line-width'] || 2,
              'line-opacity': customStyle['line-opacity'] !== undefined ? customStyle['line-opacity'] : opacity
            }
          }
        ];
      
      default:
        return {
          ...baseStyle,
          type: 'circle',
          paint: {
            'circle-radius': 4,
            'circle-color': '#007cbf',
            'circle-opacity': opacity
          }
        };
    }
  };

  const toggleLayerVisibility = (layerConfig) => {
    if (!map) return;

    const layerId = `layer-${layerConfig.dataset_id}`;
    const currentVisibility = layerVisibility.get(layerConfig.dataset_id);
    const newVisibility = !currentVisibility;

    // Toggle visibility for all related layers
    const layerIds = [layerId, layerId + '-fill', layerId + '-stroke'];
    layerIds.forEach(id => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', newVisibility ? 'visible' : 'none');
      }
    });

    // Update state
    setLayerVisibility(prev => new Map(prev.set(layerConfig.dataset_id, newVisibility)));
  };

  const handleContextMenu = (e, layer) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    setContextMenu({
      visible: true,
      x: x,
      y: y,
      layer: layer
    });
  };

  const handleContextMenuAction = (action, layer) => {
    switch (action) {
      case 'visibility':
        toggleLayerVisibility(layer);
        break;
      case 'zoom':
        // TODO: Implement zoom to layer
        break;
      default:
        break;
    }
    setContextMenu({ visible: false, x: 0, y: 0, layer: null });
  };

  // Group layers by group_id
  const groupedLayers = layers.reduce((acc, layer) => {
    const groupId = layer.group_id || 'ungrouped';
    if (!acc[groupId]) {
      acc[groupId] = {
        id: groupId,
        name: layer.group_name || 'Ungrouped',
        layers: []
      };
    }
    acc[groupId].layers.push(layer);
    return acc;
  }, {});

  const renderLayer = (layer) => {
    const isVisible = layerVisibility.get(layer.dataset_id);
    
    return (
      <div 
        key={layer.dataset_id}
        className={`shared-layer-item ${isVisible ? 'visible' : 'hidden'}`}
        onContextMenu={(e) => handleContextMenu(e, layer)}
      >
        <div className="layer-info">
          <LayerSymbologyIcon layer={layer.dataset} map={map} layerStates={layerVisibility} />
          <div className="layer-name" title={layer.dataset?.name || 'Unknown Layer'}>
            {layer.dataset?.name || 'Unknown Layer'}
          </div>
        </div>
        
        <div className="layer-controls">
          <button
            onClick={() => toggleLayerVisibility(layer)}
            className="btn-toggle-visibility"
            title={isVisible ? 'Hide layer' : 'Show layer'}
          >
            {isVisible ? '👁️' : '🙈'}
          </button>
        </div>
      </div>
    );
  };

  // Show loading overlay but still render the map container
  const showLoadingOverlay = loading;

  if (error) {
    return (
      <div className="shared-map-viewer">
        <div className="error-screen">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Map</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-map-viewer" style={{ position: 'relative' }}>
      {/* Toggle button */}
      <button
        className="shared-layers-toggle"
        onClick={() => setIsPanelOpen(v => !v)}
        title={isPanelOpen ? 'Hide Layers' : 'Show Layers'}
        aria-expanded={isPanelOpen}
      >
        {isPanelOpen ? '✖ Close' : '🗺️ Layers'}
      </button>

      {/* Backdrop on mobile when panel is open */}
      {isMobile && isPanelOpen && (
        <div className="shared-panel-backdrop" onClick={() => setIsPanelOpen(false)} />
      )}
      {showLoadingOverlay && (
        <div className="loading-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p>Loading map...</p>
          </div>
        </div>
      )}
      <div className="shared-map-layout">
        <main className="map-area">
          <div ref={mapContainer} className="map-container" />
        </main>

        <aside className={`layers-panel shared-collapsible-panel ${isPanelOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <h3>🗺️ Map Layers</h3>
          </div>
          <div className="sidebar-content">
            <div className="shared-layers-panel">
              {sharedMap.description && (
                <div className="map-description">
                  <h4>Description</h4>
                  <p>{sharedMap.description}</p>
                </div>
              )}
              
              <div className="layers-list">
                {Object.values(groupedLayers).map(group => (
                  <div key={group.id} className="layer-group" style={{ marginBottom: '15px' }}>
                    {group.name !== 'Ungrouped' && (
                      <div className="group-header" style={{
                        padding: '8px 0',
                        borderBottom: '1px solid #eee',
                        marginBottom: '8px'
                      }}>
                        <span className="group-name" style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333'
                        }}>{group.name}</span>
                      </div>
                    )}
                    <div className="group-layers">
                      {group.layers.map(renderLayer)}
                    </div>
                  </div>
                ))}
              </div>
              
              {layers.length === 0 && (
                <div className="no-layers" style={{
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '14px',
                  padding: '20px'
                }}>
                  No layers available in this shared map.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="layer-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 1000
          }}
        >
          <div className="context-menu-item" onClick={() => handleContextMenuAction('visibility', contextMenu.layer)}>
            <span className="context-menu-icon">
              {layerVisibility.get(contextMenu.layer?.dataset_id) ? '🙈' : '👁️'}
            </span>
            {layerVisibility.get(contextMenu.layer?.dataset_id) ? 'Hide Layer' : 'Show Layer'}
          </div>
          <div className="context-menu-item" onClick={() => handleContextMenuAction('zoom', contextMenu.layer)}>
            <span className="context-menu-icon">🎯</span>
            Zoom to Layer
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedMapViewer;
